import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { HERO_IMAGE, SITE } from "../data/content";

export const Hero = () => {
  const scrollTo = (href) =>
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center">
      <div className="absolute inset-0">
        <img src={HERO_IMAGE} alt="Clean property" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-primary/70 mix-blend-multiply" />
        <div className="absolute inset-0 bg-black/30" />
      </div>

      <div className="relative px-6 md:px-12 lg:px-24 py-32 w-full">
        <div className="max-w-3xl">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 text-sm font-medium text-white/90 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-6"
          >
            <CheckCircle2 className="w-4 h-4" /> Locally owned & operated in {SITE.location}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.05]"
          >
            We Load It. <span className="text-[hsl(var(--brand-green))]">We Haul It.</span> We Clean It.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 text-lg text-white/85 max-w-xl"
          >
            {SITE.subTagline} Professional junk removal & full property cleanouts serving Edmonton & surrounding areas—all-inclusive pricing, no hidden fees.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Button
              data-testid="hero-quote-button"
              onClick={() => scrollTo("#quote")}
              size="lg"
              className="rounded-sm bg-[hsl(var(--brand-green))] text-white hover:bg-white hover:text-primary transition-colors h-12 px-7 text-base font-semibold group"
            >
              Get a Free Quote
              <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button
              data-testid="hero-call-button"
              asChild
              size="lg"
              variant="outline"
              className="rounded-sm bg-transparent border-white/40 text-white hover:bg-white/10 h-12 px-7 text-base font-semibold"
            >
              <a href={`tel:${SITE.contacts[0].tel}`}>
                <Phone className="w-4 h-4 mr-1" /> Call or Text {SITE.contacts[0].phone}
              </a>
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mt-12 flex flex-wrap gap-x-8 gap-y-3 text-sm text-white/80"
          >
            {["Same-day & next-day service", "All labour included", "Eco-friendly disposal"].map((t) => (
              <span key={t} className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-[hsl(var(--brand-green))]" /> {t}
              </span>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
